// Task selection and labelling helpers for the Home cards.
import { differenceInCalendarDays, isSameDay } from 'date-fns';
import type { Task } from '@shared/types';
import { isCompleteType } from '@shared/constants';
import type { Tables } from '@/lib/store';
import { statusOf } from '@/lib/hierarchy';
import { dueTone, formatDateShort, formatDue, type DueTone } from '@/lib/format';

/** Live (not archived, list still present) tasks of a workspace. */
export function workspaceTasks(db: Tables, ws: string): Task[] {
  return Object.values(db.tasks).filter((t) => {
    if (t.archived || t.workspaceId !== ws) return false;
    const list = db.lists[t.listId];
    return !!list && !list.archived;
  });
}

export function isClosed(db: Tables, t: Task) {
  return isCompleteType(statusOf(db, t).type);
}

/** "Space / Folder / List" */
export function taskTrail(db: Tables, t: Task): string[] {
  const list = db.lists[t.listId];
  if (!list) return [];
  const out: string[] = [];
  const space = db.spaces[list.spaceId];
  if (space) out.push(space.name);
  if (list.folderId && db.folders[list.folderId]) out.push(db.folders[list.folderId].name);
  out.push(list.name);
  return out;
}

const day = (ts: number) => differenceInCalendarDays(ts, Date.now());

/** "9/1/26 - 12/29/26", "Started 7/6/26", "Yesterday - Fri", "Tomorrow" */
export function dateRangeLabel(t: Task, closed = false): { text: string; tone: DueTone } {
  const { startDate: start, dueDate: due } = t;
  const tone = dueTone(due, closed);
  if (start !== null && due !== null) {
    if (isSameDay(start, due)) return { text: formatDue(due), tone };
    return { text: `${formatDue(start)} - ${formatDue(due)}`, tone };
  }
  if (start !== null) return { text: day(start) <= 0 ? `Started ${formatDateShort(start)}` : `Starts ${formatDue(start)}`, tone: 'normal' };
  if (due !== null) return { text: formatDue(due), tone };
  return { text: '', tone: 'normal' };
}

export type WorkGroupKey = 'today' | 'overdue' | 'next' | 'unscheduled' | 'later';

export const WORK_GROUPS: { key: WorkGroupKey; label: string }[] = [
  { key: 'today', label: 'Today' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'next', label: 'Next' },
  { key: 'unscheduled', label: 'Unscheduled' },
  { key: 'later', label: 'Later' },
];

export function workGroup(t: Task): WorkGroupKey {
  if (t.dueDate !== null && day(t.dueDate) < 0) return 'overdue';
  if (t.dueDate !== null && day(t.dueDate) === 0) return 'today';
  if (t.startDate !== null && day(t.startDate) <= 0) return 'today';
  if (t.dueDate === null && t.startDate === null) return 'unscheduled';
  const first = Math.min(...[t.startDate, t.dueDate].filter((x): x is number => x !== null));
  return day(first) <= 7 ? 'next' : 'later';
}

/** Earliest-due first, then priority (urgent first), then name. */
export function byDueThenPriority(a: Task, b: Task) {
  const ad = a.dueDate ?? Number.POSITIVE_INFINITY;
  const bd = b.dueDate ?? Number.POSITIVE_INFINITY;
  if (ad !== bd) return ad - bd;
  const ap = a.priority ?? 9;
  const bp = b.priority ?? 9;
  if (ap !== bp) return ap - bp;
  return a.name.localeCompare(b.name);
}

export function byPriorityThenDue(a: Task, b: Task) {
  const ap = a.priority ?? 9;
  const bp = b.priority ?? 9;
  if (ap !== bp) return ap - bp;
  const ad = a.dueDate ?? Number.POSITIVE_INFINITY;
  const bd = b.dueDate ?? Number.POSITIVE_INFINITY;
  if (ad !== bd) return ad - bd;
  return a.name.localeCompare(b.name);
}

export function isDueToday(t: Task) {
  return t.dueDate !== null && day(t.dueDate) === 0;
}
